import React, { useMemo, useState } from "react";
import { Box, Button, Chip, IconButton, Stack, Typography } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddIcon from "@mui/icons-material/Add";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import MenuIcon from "@mui/icons-material/Menu";
import PhotoLibraryIcon from "@mui/icons-material/PhotoLibrary";
import { Outlet, useNavigate, useOutletContext, useParams } from "react-router-dom";
import ItemLibrary from "./ItemLibrary";
import LessonsManager from "./lessons/LessonsManager";
import TaxonomyManager from "./taxonomy/TaxonomyManager";
import AssetManager from "./assets/AssetManager";
import AssetQuickSearchDialog from "./assets/AssetQuickSearchDialog";
import EnrollmentsManager from "./enrollments/EnrollmentsManager";
import ContactsManager from "./contacts/ContactsManager";

const AuthoringWorkspace = () => {
  const {
    currentSection,
    currentType,
    openHelpPanel = () => {},
    isHelpPanelOpen = false,
    openNavigationPanel = () => {},
    isNavigationPanelAvailable = false,
    lessonsNav = null,
  } = useOutletContext();
  const navigate = useNavigate();
  const { itemId } = useParams();
  const showDetails = Boolean(itemId);
  const isItemsSection = currentSection?.key === "items";
  const isLessonsSection = currentSection?.key === "lessons";
  const isEnrollmentsSection = currentSection?.key === "enrollments";
  const isContactsSection = currentSection?.key === "contacts";
  const showBackToLibrary = isItemsSection && showDetails;
  const isTaxonomySection = currentSection?.key === "taxonomy";
  const showCreateButton = isItemsSection && !showDetails;
  const showAssetSearchButton = isItemsSection || isLessonsSection;
  const showCancelButton = isItemsSection || isLessonsSection || isTaxonomySection;
  const [isAssetSearchOpen, setIsAssetSearchOpen] = useState(false);
  const assetQuickSearchTitle = useMemo(() => {
    if (isItemsSection) {
      return "Find an asset for this item";
    }

    if (isLessonsSection) {
      return "Find an asset for this lesson";
    }

    return "Find an asset";
  }, [isItemsSection, isLessonsSection]);

  const handleBackToLibrary = () => {
    const typeKey = currentType?.key || "mcma";
    navigate(`/authoring/items/${typeKey}`);
  };

  const handleCreateNewItem = () => {
    const typeKey = currentType?.key || "mcma";
    navigate(`/authoring/items/${typeKey}/item/new`);
  };

  const handleCancelAuthoring = () => {
    if (window.history && window.history.length > 1) {
      navigate(-1);
      return;
    }

    navigate("/coursepage");
  };

  const handleOpenAssetSearch = () => {
    setIsAssetSearchOpen(true);
  };

  const handleCloseAssetSearch = () => {
    setIsAssetSearchOpen(false);
  };

  if (!currentSection || !currentType) {
    return null;
  }

  return (
    <Stack spacing={3} sx={{ width: "100%", maxWidth: "100%" }}>
      <Box>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="space-between"
          spacing={2}
          sx={{ mb: 2 }}
        >
          <Stack spacing={showBackToLibrary ? 1 : 0.5} alignItems="flex-start">
            <Stack direction="row" alignItems="center" spacing={1}>
              {isNavigationPanelAvailable && (
                <IconButton
                  color="primary"
                  onClick={openNavigationPanel}
                  aria-label="Open navigation menu"
                  size="small"
                  sx={{ display: { xs: "inline-flex", md: "none" } }}
                >
                  <MenuIcon />
                </IconButton>
              )}
              <Chip
                label={currentSection.label}
                color="primary"
                variant="outlined"
                size="small"
              />
              <Typography variant="overline" color="text.secondary">
                {currentType.navLabel}
              </Typography>
            </Stack>
            {showBackToLibrary && (
              <Button
                size="small"
                startIcon={<ArrowBackIcon fontSize="small" />}
                onClick={handleBackToLibrary}
              >
                Back To Library
              </Button>
            )}
          </Stack>
          <Stack direction="row" spacing={1}>
            {showCancelButton && (
              <Button onClick={handleCancelAuthoring}>Cancel</Button>
            )}
            {showAssetSearchButton && (
              <Button
                variant="outlined"
                color="primary"
                size="medium"
                startIcon={<PhotoLibraryIcon />}
                onClick={handleOpenAssetSearch}
              >
                Browse assets
              </Button>
            )}
            {showCreateButton && (
              <Button
                variant="contained"
                color="primary"
                size="medium"
                startIcon={<AddIcon />}
                onClick={handleCreateNewItem}
              >
                Create
              </Button>
            )}
            {!isHelpPanelOpen && (
              <IconButton
                color="primary"
                size="medium"
                onClick={openHelpPanel}
                aria-label="Open design checklist"
              >
                <HelpOutlineIcon />
              </IconButton>
            )}
          </Stack>
        </Stack>
        {!showDetails &&
          !isTaxonomySection &&
          !isLessonsSection &&
          currentSection.key !== "assets" && (
          <>
            <Typography variant="h4" sx={{ fontWeight: 600 }} gutterBottom>
              {currentType.title}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {currentType.description}
            </Typography>
          </>
        )}
      </Box>

      {showDetails ? (
        <Outlet
          context={{
            currentSection,
            currentType,
            openAssetBrowser: handleOpenAssetSearch,
          }}
        />
      ) : (
        <>
          {currentSection.key === "items" && (
            <ItemLibrary currentType={currentType} />
          )}
          {currentSection.key === "lessons" && (
            <LessonsManager
              currentType={currentType}
              courses={lessonsNav?.courses}
              coursesLoading={lessonsNav?.loading}
              coursesError={lessonsNav?.error}
              selectedCourseId={lessonsNav?.selectedCourseId}
              onSelectCourse={lessonsNav?.selectCourse}
              onRefreshCourses={lessonsNav?.refreshCourses}
              isNavigationCollapsed={lessonsNav?.isNavigationCollapsed}
              onExpandNavigation={lessonsNav?.expandNavigation}
              onCollapseNavigation={lessonsNav?.collapseNavigation}
              onToggleNavigationCollapse={lessonsNav?.toggleNavigationCollapsed}
              setNavigationCollapsible={lessonsNav?.setNavigationCollapsible}
            />
          )}
          {isEnrollmentsSection && (
            <EnrollmentsManager
              defaultYear={Number(currentType?.defaultYear) || 2026}
            />
          )}
          {isContactsSection && <ContactsManager />}
          {isTaxonomySection && <TaxonomyManager currentType={currentType} />}
          {currentSection.key === "assets" && <AssetManager />}
      </>
      )}
      <AssetQuickSearchDialog
        open={isAssetSearchOpen}
        onClose={handleCloseAssetSearch}
        title={assetQuickSearchTitle}
      />
    </Stack>
  );
};

export default AuthoringWorkspace;
